import { FC, useState } from 'react'
import {
    Alert,
    Badge,
    Button,
    Col,
    Form,
    Row,
    Spinner,
} from 'react-bootstrap'
import { Dialog } from 'primereact/dialog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faArrowLeft,
    faBell,
    faChartLine,
    faCheckCircle,
    faClipboardList,
    faCubes,
    faFileExport,
    faHandHoldingDollar,
    faList,
    faPaperPlane,
    faShieldAlt,
    faUsers,
} from '@fortawesome/free-solid-svg-icons'
import { api } from '../../../../auth'
import { useToast } from '../../../common/toast/hooks/useToast'

type ReportItemConfig = {
    key: string
    label: string
    icon: any
    color: string
}

type ReportSectionConfig = {
    key: string
    label: string
    description: string
    icon: any
    gradient: string
    accentColor: string
    items: ReportItemConfig[]
}

const REPORT_SECTIONS: ReportSectionConfig[] = [
    {
        key: 'plan',
        label: 'Plan Information',
        description: 'Your current plan, resource usage, and capacity overview',
        icon: faShieldAlt,
        gradient: 'linear-gradient(135deg, #0a63ea, #3b82f6)',
        accentColor: '#0a63ea',
        items: [
            { key: 'plan_details', label: 'Plan Details', icon: faHandHoldingDollar, color: '#0a63ea' },
            { key: 'modules_used', label: 'Modules Used', icon: faCubes, color: '#f59e0b' },
            { key: 'pages_count', label: 'Pages Count', icon: faClipboardList, color: '#10b981' },
        ],
    },
    {
        key: 'payments',
        label: 'Financial — Payments',
        description: 'Transaction volumes, status breakdown, and full records table',
        icon: faChartLine,
        gradient: 'linear-gradient(135deg, #10b981, #059669)',
        accentColor: '#10b981',
        items: [
            { key: 'payment_stats', label: 'Payment Statistics', icon: faChartLine, color: '#10b981' },
            { key: 'payment_records', label: 'All Payment Records', icon: faList, color: '#059669' },
        ],
    },
    {
        key: 'subscriptions',
        label: 'Subscriptions',
        description: 'Subscriber activity, status distribution, and full records table',
        icon: faUsers,
        gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
        accentColor: '#8b5cf6',
        items: [
            { key: 'subscription_stats', label: 'Subscription Statistics', icon: faUsers, color: '#8b5cf6' },
            { key: 'subscription_records', label: 'All Subscription Records', icon: faList, color: '#7c3aed' },
        ],
    },
]

const PERIOD_OPTIONS = [
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 9, label: '9 months' },
    { value: 12, label: '12 months' },
]

interface GenerateReportModalProps {
    show: boolean
    onHide: () => void
}

const GenerateReportModal: FC<GenerateReportModalProps> = ({ show, onHide }) => {
    const axiosPrivate = api
    const { triggerToast } = useToast()

    const [enabledSections, setEnabledSections] = useState<Record<string, boolean>>({
        plan: true,
        payments: true,
        subscriptions: true,
    })
    const [enabledItems, setEnabledItems] = useState<Record<string, boolean>>({
        plan_details: true,
        modules_used: true,
        pages_count: true,
        payment_stats: true,
        payment_records: true,
        subscription_stats: true,
        subscription_records: true,
    })
    const [months, setMonths] = useState(6)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [errMsg, setErrMsg] = useState('')

    const totalEnabledItems = Object.values(enabledItems).filter(Boolean).length
    const totalEnabledSections = Object.values(enabledSections).filter(Boolean).length

    const toggleSection = (sectionKey: string) => {
        const next = !enabledSections[sectionKey]
        setEnabledSections((prev) => ({ ...prev, [sectionKey]: next }))

        const section = REPORT_SECTIONS.find((s) => s.key === sectionKey)
        if (section) {
            const updated = { ...enabledItems }
            section.items.forEach((item) => {
                updated[item.key] = next
            })
            setEnabledItems(updated)
        }
    }

    const toggleItem = (sectionKey: string, itemKey: string) => {
        const next = !enabledItems[itemKey]
        setEnabledItems((prev) => ({ ...prev, [itemKey]: next }))

        const section = REPORT_SECTIONS.find((s) => s.key === sectionKey)
        if (section) {
            const anyEnabled = section.items.some((item) =>
                item.key === itemKey ? next : enabledItems[item.key]
            )
            setEnabledSections((prev) => ({ ...prev, [sectionKey]: anyEnabled }))
        }
    }

    const buildPayload = () => {
        const sections: Record<string, any> = {}
        REPORT_SECTIONS.forEach((section) => {
            if (enabledSections[section.key]) {
                sections[section.key] = {
                    enabled: true,
                    items: section.items
                        .filter((item) => enabledItems[item.key])
                        .map((item) => item.key),
                }
            }
        })
        return { sections, months }
    }

    const handleGenerate = () => {
        if (totalEnabledItems === 0) {
            setErrMsg('Please select at least one item to include in the report.')
            return
        }
        setErrMsg('')
        setShowConfirm(true)
    }

    const handleConfirmGenerate = async () => {
        setIsSubmitting(true)
        setErrMsg('')

        try {
            const response = await axiosPrivate.post(
                '/dashboard/report/generate',
                buildPayload()
            )

            if (response.data?.success) {
                triggerToast({
                    severity: 'success',
                    summary: 'Report Queued',
                    detail: 'Your report is being generated in the background. You will be notified when it is ready.',
                })
                setShowConfirm(false)
                onHide()
            } else {
                setErrMsg(response.data?.errors?.join(', ') || 'Failed to generate report')
            }
        } catch (err: any) {
            setErrMsg(
                err?.response?.data?.errors?.join(', ') ||
                'Something went wrong. Please try again.'
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setErrMsg('')
        setShowConfirm(false)
        onHide()
    }

    const reportDialogHeader = (
        <div className="report-modal__hero">
            <div className="report-modal__hero-bg" />
            <div className="report-modal__hero-content">
                <div className="report-modal__hero-icon">
                    <FontAwesomeIcon icon={faFileExport} />
                </div>
                <div>
                    <h4 className="report-modal__hero-title">Generate System Report</h4>
                    <p className="report-modal__hero-subtitle">
                        Configure and compile your custom report &mdash; delivered as PDF &amp; CSV
                    </p>
                </div>
            </div>
        </div>
    )

    return (
        <Dialog
            className="report-modal"
            header={reportDialogHeader}
            visible={show}
            onHide={handleClose}
            style={{ width: 'min(95vw, 1140px)' }}
            breakpoints={{ '960px': '98vw' }}
            dismissableMask={false}
            closable={true}
            maximizable
            footer={
                <div className="report-modal__footer">
                    {showConfirm ? (
                        <>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowConfirm(false)}
                                className="d-inline-flex align-items-center gap-2"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                                Back
                            </Button>
                            <Button
                                variant="success"
                                onClick={handleConfirmGenerate}
                                disabled={isSubmitting}
                                className="d-inline-flex align-items-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner animation="border" size="sm" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        Confirm &amp; Generate
                                    </>
                                )}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline-secondary" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleGenerate}
                                disabled={totalEnabledItems === 0}
                                className="d-inline-flex align-items-center gap-2"
                            >
                                <FontAwesomeIcon icon={faPaperPlane} />
                                Generate Report
                            </Button>
                        </>
                    )}
                </div>
            }
        >
            <div className="report-modal__body">
                {errMsg && (
                    <Alert variant="danger" dismissible onClose={() => setErrMsg('')}>
                        {errMsg}
                    </Alert>
                )}

                {showConfirm ? (
                    <div className="report-modal__confirm">
                        <div className="report-modal__confirm-icon">
                            <FontAwesomeIcon icon={faCheckCircle} />
                        </div>
                        <h5>Confirm Report Generation</h5>
                        <p className="text-muted">
                            Please review your selection before starting. This process runs in the
                            background and may take a while depending on data volume.
                        </p>

                        <div className="report-modal__confirm-summary">
                            <div className="report-modal__confirm-rows">
                                <div className="report-modal__confirm-row">
                                    <span className="report-modal__confirm-label">Sections</span>
                                    <span className="report-modal__confirm-value">{totalEnabledSections}</span>
                                </div>
                                <div className="report-modal__confirm-row">
                                    <span className="report-modal__confirm-label">Data points</span>
                                    <span className="report-modal__confirm-value">{totalEnabledItems}</span>
                                </div>
                                <div className="report-modal__confirm-row">
                                    <span className="report-modal__confirm-label">Period</span>
                                    <span className="report-modal__confirm-value">Last {months} months</span>
                                </div>
                                <div className="report-modal__confirm-row">
                                    <span className="report-modal__confirm-label">Output</span>
                                    <span className="report-modal__confirm-value">PDF &amp; CSV</span>
                                </div>
                            </div>

                            <hr />

                            <div className="report-modal__confirm-sections">
                                {REPORT_SECTIONS.filter((s) => enabledSections[s.key]).map((section) => {
                                    const activeItems = section.items.filter((item) => enabledItems[item.key])
                                    return (
                                        <div key={section.key} className="report-modal__confirm-section">
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <div
                                                    className="report-modal__composition-dot"
                                                    style={{ background: section.accentColor }}
                                                />
                                                <strong className="small">{section.label}</strong>
                                            </div>
                                            <ul className="report-modal__confirm-items">
                                                {activeItems.map((item) => (
                                                    <li key={item.key}>
                                                        <FontAwesomeIcon
                                                            icon={item.icon}
                                                            className="me-1"
                                                            style={{ color: item.color, fontSize: '0.75rem' }}
                                                        />
                                                        {item.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                ) : (

                <Row>
                    <Col lg={7}>
                        <div className="report-modal__config">
                            <div className="report-modal__config-header">
                                <h6>Report Sections</h6>
                                <small className="text-muted">
                                    {totalEnabledItems} item{totalEnabledItems !== 1 ? 's' : ''} selected
                                </small>
                            </div>

                            {REPORT_SECTIONS.map((section) => {
                                const isEnabled = enabledSections[section.key]
                                const sectionItemsEnabled = section.items.filter(
                                    (item) => enabledItems[item.key]
                                ).length

                                return (
                                    <div
                                        key={section.key}
                                        className={`report-section-card ${isEnabled ? 'report-section-card--active' : ''}`}
                                    >
                                        <div
                                            className="report-section-card__accent"
                                            style={{
                                                background: isEnabled ? section.gradient : '#d1d5db',
                                            }}
                                        />
                                        <div className="report-section-card__header">
                                            <div className="report-section-card__info">
                                                <div
                                                    className="report-section-card__icon"
                                                    style={{
                                                        background: isEnabled ? section.gradient : '#e5e7eb',
                                                        color: isEnabled ? '#fff' : '#9ca3af',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={section.icon} />
                                                </div>
                                                <div>
                                                    <h6 className="report-section-card__title">
                                                        {section.label}
                                                    </h6>
                                                    <small className="report-section-card__desc">
                                                        {section.description}
                                                    </small>
                                                </div>
                                            </div>
                                            <Form.Check
                                                type="switch"
                                                id={`section-${section.key}`}
                                                checked={isEnabled}
                                                onChange={() => toggleSection(section.key)}
                                                className="report-section-card__switch"
                                            />
                                        </div>

                                        <div
                                            className={`report-section-card__items ${isEnabled ? 'report-section-card__items--visible' : ''}`}
                                        >
                                            {section.items.map((item) => (
                                                <label
                                                    key={item.key}
                                                    className={`report-item ${enabledItems[item.key] ? 'report-item--active' : ''}`}
                                                    htmlFor={`item-${item.key}`}
                                                >
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`item-${item.key}`}
                                                        checked={enabledItems[item.key] || false}
                                                        onChange={() => toggleItem(section.key, item.key)}
                                                        className="report-item__checkbox"
                                                    />
                                                    <FontAwesomeIcon
                                                        icon={item.icon}
                                                        className="report-item__icon"
                                                        style={{ color: enabledItems[item.key] ? item.color : '#9ca3af' }}
                                                    />
                                                    <span className="report-item__label">{item.label}</span>
                                                </label>
                                            ))}

                                            {isEnabled && sectionItemsEnabled < section.items.length && (
                                                <small className="text-muted ps-2" style={{ gridColumn: '1 / -1' }}>
                                                    {sectionItemsEnabled} of {section.items.length} items selected
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </Col>

                    <Col lg={5}>
                        <div className="report-modal__sidebar">
                            <div className="report-modal__sidebar-section">
                                <h6>Date Range</h6>
                                <p className="text-muted small">
                                    For payments and subscription data
                                </p>
                                <div className="report-modal__period-grid">
                                    {PERIOD_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            className={`report-modal__period-btn ${months === opt.value ? 'report-modal__period-btn--active' : ''}`}
                                            onClick={() => setMonths(opt.value)}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="report-modal__sidebar-section">
                                <h6>Report Composition</h6>
                                <div className="report-modal__composition">
                                    {REPORT_SECTIONS.map((section) => {
                                        const sectionItemsEnabled = section.items.filter(
                                            (item) => enabledItems[item.key]
                                        ).length
                                        const isEnabled = enabledSections[section.key]

                                        return (
                                            <div
                                                key={section.key}
                                                className={`report-modal__composition-row ${isEnabled ? '' : 'report-modal__composition-row--disabled'}`}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <div
                                                        className="report-modal__composition-dot"
                                                        style={{
                                                            background: isEnabled ? section.accentColor : '#d1d5db',
                                                        }}
                                                    />
                                                    <span>{section.label}</span>
                                                </div>
                                                <Badge
                                                    bg={isEnabled ? 'primary' : 'secondary'}
                                                    className="report-modal__composition-badge"
                                                >
                                                    {sectionItemsEnabled}/{section.items.length}
                                                </Badge>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            <div className="report-modal__sidebar-section">
                                <h6>Output Format</h6>
                                <div className="report-modal__format-info">
                                    <FontAwesomeIcon icon={faFileExport} className="text-primary me-2" />
                                    <span>PDF &amp; CSV</span>
                                </div>
                            </div>

                            <div className="report-modal__sidebar-section report-modal__sidebar-section--info">
                                <FontAwesomeIcon icon={faBell} className="text-warning me-2" />
                                <span className="small">
                                    Reports are generated in the background. You will receive a
                                    notification with download links when ready.
                                </span>
                            </div>
                        </div>
                    </Col>
                </Row>

                )}
            </div>
        </Dialog>
    )
}

export default GenerateReportModal
